<template>
  <component
    :is="manualInput ? 'v-combobox' : 'v-autocomplete'"
    ref="input"
    v-model="model"
    :items="suggestions"
    :search-input.sync="searchInput"
    :label="label"
    :placeholder="computedPlaceholder"
    :loading="isLoading"
    item-text="text"
    item-value="value"
    :return-object="false"
    :multiple="multiple"
    :chips="multiple"
    :small-chips="multiple"
    :deletable-chips="multiple"
    :clearable="clearable"
    :disabled="disabled"
    :readonly="readonly"
    :outlined="outlined"
    :dense="dense"
    :hide-details="hideDetails"
    :error-messages="errorMessages"
    @keydown.enter.native.capture="onEnterCapture"
    @keydown.enter.native="onEnterBubble"
    @blur="$emit('blur', $event)"
  >
    <!-- Free text is accepted on Enter, but nothing in the UI says so: surface the
         typed value as an explicit entry at the top of the list. Kept out of `items`
         on purpose, so it never goes through the Vuetify filtering nor shows up as
         the label of the resulting chip. -->
    <template v-if="manualInput" #prepend-item>
      <v-list-item v-if="canAddTypedValue" @click.stop="addTypedValue">
        <v-list-item-icon class="mr-2">
          <v-icon color="primary">mdi-plus-circle</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            {{ $t('components.locationSuggestion.addValue', { value: typedValue }) }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>

    <!-- Vuetify appends its no-data tile before the prepend-item, so an unknown
         location would read "Add ..." followed by "No location found". The slot
         drops it while the add entry stands for it.
         The empty node matters: a combobox only opens its menu on an empty list
         when this slot renders something, and a slot holding nothing but a v-if
         comment counts as absent. Without it the add entry would be unreachable
         precisely when it is the only thing left to show. -->
    <template #no-data>
      <v-list-item v-if="!canAddTypedValue">
        <v-list-item-content>
          <v-list-item-title>{{ $t('components.select.noDataText') }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <div v-else />
    </template>

    <!-- Flag the labelled suggestions in the list, the default rendering being
         kept when no location carries a label. -->
    <template v-if="hasLabelledSuggestion" #item="{ item, attrs, on }">
      <v-list-item v-bind="attrs" v-on="on">
        <v-list-item-action v-if="multiple">
          <!-- The checkbox stops the click propagation, so it toggles the item itself. -->
          <v-simple-checkbox :value="attrs.inputValue" :ripple="false" color="primary" @input="on.click" />
        </v-list-item-action>
        <v-list-item-content>
          <div class="d-flex align-center">
            <span>{{ item.text }}</span>
            <v-chip v-if="item.label" class="ml-2 px-3 text-none" small outlined color="primary">
              {{ item.label }}
            </v-chip>
          </div>
        </v-list-item-content>
      </v-list-item>
    </template>
  </component>
</template>

<script>
  import { LocationService } from '@/services/locationService'

  // Locations are shared by every instance, so concurrent mounts (a filter and the
  // dialog it opens, two filters in the same dialog...) reuse the request in flight
  // instead of each firing its own. The promise is dropped once settled: a later
  // mount fetches again and never serves a stale list.
  // Keyed by scope, as two instances asking for different locations must never
  // read each other's answer.
  const pendingRequests = new Map()

  function fetchLocations(accessToken, locationIds) {
    const scopeKey = locationIds ? [...locationIds].sort().join(',') : ''
    if (!pendingRequests.has(scopeKey)) {
      const queryStringParameters = {
        itemsPerPage: false,
        fields: ['location_id', 'name'],
      }
      if (locationIds) {
        queryStringParameters.filters = JSON.stringify({ location_id: locationIds })
      }
      pendingRequests.set(
        scopeKey,
        LocationService.getLocations(accessToken, queryStringParameters).finally(() => {
          pendingRequests.delete(scopeKey)
        })
      )
    }
    return pendingRequests.get(scopeKey)
  }

  export default {
    name: 'OLocationSuggestionInput',
    props: {
      value: {
        type: [String, Array],
        default: null,
      },
      /**
       * Free text is accepted by default, the suggestions being only a helper.
       * Set it to false to restrict the input to the suggested locations.
       */
      manualInput: {
        type: Boolean,
        default: true,
      },
      /**
       * Locations used to build the suggestion list. When omitted, the component
       * fetches them itself, like OSelectLocations does.
       */
      items: {
        type: Array,
        default: null,
      },
      /**
       * Location ids the user is allowed to see, restricting the suggestions to
       * them. The locations API answers with every location of the partner to
       * anyone holding a valid token, so a caller displaying this input on a
       * screen scoped to a few stores has to say so here.
       * Null suggests them all, and an empty array suggests none.
       */
      locationIds: {
        type: Array,
        default: null,
      },
      /**
       * Extra locations merged on top of the fetched ones, for suggestions the
       * locations API does not know about. Same shape as `items`, `name` being
       * optional, plus an optional `label` flagging the location in the list
       * with a chip, the way the country area form flags a newly typed area.
       * They come first in the list and win over a fetched location sharing
       * their `location_id`.
       */
      extraItems: {
        type: Array,
        default: () => [],
      },
      label: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: '',
      },
      multiple: {
        type: Boolean,
        default: false,
      },
      clearable: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      readonly: {
        type: Boolean,
        default: false,
      },
      outlined: {
        type: Boolean,
        default: false,
      },
      dense: {
        type: Boolean,
        default: true,
      },
      hideDetails: {
        type: [Boolean, String],
        default: true,
      },
      errorMessages: {
        type: [String, Array],
        default: () => [],
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        locations: this.items ?? [],
        loadingLocations: false,
        searchInput: '',
        // Whether the Enter being handled was aimed at this input, snapshotted
        // before Vuetify reacts to it. See onEnterCapture.
        enterBelongsToInput: false,
      }
    },
    async mounted() {
      if (this.items || !this.$auth.accessToken) {
        return
      }
      // An empty scope grants access to no location at all, so there is nothing
      // to ask for. Guarded here rather than sent as an empty filter, which the
      // API would not read as "none".
      if (this.locationIds?.length === 0) {
        return
      }
      this.loadingLocations = true
      try {
        const res = await fetchLocations(this.$auth.accessToken, this.locationIds)
        this.locations = res?.data ?? []
      } finally {
        this.loadingLocations = false
      }
    },
    computed: {
      model: {
        get() {
          if (this.multiple) {
            return this.value ?? []
          }
          return this.value ?? null
        },
        set(value) {
          if (!this.multiple) {
            return this.$emit('input', value ? this.canonicalValue(value) : null)
          }
          const values = []
          for (const item of value ?? []) {
            const canonicalItem = this.canonicalValue(item)
            if (!values.includes(canonicalItem)) {
              values.push(canonicalItem)
            }
          }
          this.$emit('input', values)
        },
      },
      isLoading() {
        return this.loading || this.loadingLocations
      },
      computedPlaceholder() {
        if (this.placeholder) {
          return this.placeholder
        }
        // Free text is not discoverable from the label alone, so say it upfront.
        return this.manualInput ? this.$t('components.locationSuggestion.placeholder') : ''
      },
      suggestions() {
        const suggestionsById = new Map()
        for (const location of [...this.extraItems, ...this.locations]) {
          const locationId = location?.location_id
          if (!locationId || suggestionsById.has(locationId)) {
            continue
          }
          suggestionsById.set(locationId, {
            text: location.name ? `${locationId} - ${location.name}` : locationId,
            value: locationId,
            label: location.label ?? '',
          })
        }
        return [...suggestionsById.values()]
      },
      hasLabelledSuggestion() {
        return this.suggestions.some((suggestion) => suggestion.label)
      },
      selectedValues() {
        if (Array.isArray(this.value)) {
          return this.value
        }
        return this.value ? [this.value] : []
      },
      typedValue() {
        return this.searchInput?.trim() ?? ''
      },
      // Lookup of the known locations by lowercased id, and by lowercased displayed
      // text since single mode puts the selected text back in the search. Case is
      // ignored on purpose: locations carry no common convention (an API location_id
      // keeps its own case, a virtual location is the free text an admin typed in the
      // partner settings), and the app displays them all uppercased anyway
      // (App.vue forces `text-transform: uppercase` on the body), so what the user
      // reads never tells the real case.
      suggestionsByLowerKey() {
        const byLowerKey = new Map()
        for (const suggestion of this.suggestions) {
          byLowerKey.set(suggestion.text.toLowerCase(), suggestion.value)
        }
        // Ids win over displayed texts on the unlikely collision between the two.
        for (const suggestion of this.suggestions) {
          byLowerKey.set(suggestion.value.toLowerCase(), suggestion.value)
        }
        return byLowerKey
      },
      canAddTypedValue() {
        const lowerTypedValue = this.typedValue.toLowerCase()
        if (!this.manualInput || !lowerTypedValue || this.suggestionsByLowerKey.has(lowerTypedValue)) {
          return false
        }
        return !this.selectedValues.some((value) => String(value).toLowerCase() === lowerTypedValue)
      },
    },
    watch: {
      items(newValue) {
        if (newValue) {
          this.locations = newValue
        }
      },
    },
    methods: {
      // A location the user typed in another case than the one it is stored with
      // would not match anything server side, so it is sent back canonicalised.
      // An unknown value is only trimmed: with no case convention to rely on,
      // rewriting it would be a guess.
      canonicalValue(value) {
        if (typeof value !== 'string') {
          return value
        }
        const trimmedValue = value.trim()
        return this.suggestionsByLowerKey.get(trimmedValue.toLowerCase()) ?? trimmedValue
      },
      addTypedValue() {
        if (!this.typedValue) {
          return
        }
        // The model setter canonicalises and deduplicates what it is given.
        if (this.multiple) {
          this.model = [...this.selectedValues, this.typedValue]
        } else {
          this.model = this.typedValue
          this.$refs.input?.blur()
        }
        this.searchInput = ''
      },
      // Enter is read twice. In the capture phase nothing is done yet, so the input
      // state still tells whether the key was aimed at it; by the time the event
      // bubbles back Vuetify has consumed it and cleared the search.
      onEnterCapture() {
        this.enterBelongsToInput = Boolean(this.typedValue) || Boolean(this.$refs.input?.isMenuActive)
      },
      // Bubble phase: Vuetify has done its own work (value added, suggestion picked),
      // we only keep the key from reaching a dialog hosting the input, which would
      // otherwise take it as a validation and close on the first Enter.
      onEnterBubble(event) {
        if (this.enterBelongsToInput) {
          event.stopPropagation()
          this.closeMenu()
        }
        this.enterBelongsToInput = false
      },
      // Enter means "done with this value", so the suggestions step aside: they hide
      // the rest of a filter dialog, and Vuetify reopens the menu on every Enter
      // (VSelect.onKeyDown calls activateMenu), which would swallow all the following
      // ones and leave no way to validate a hosting dialog from the keyboard. Typing
      // reopens the menu, so chaining several values costs nothing.
      closeMenu() {
        // Deferred so Vuetify's own nextTick, the one clicking the highlighted
        // suggestion, still runs while the menu is open.
        this.$nextTick(() => {
          if (this.$refs.input) {
            this.$refs.input.isMenuActive = false
          }
        })
      },
    },
  }
</script>
